"use client"

import * as React from "react"
import { XIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { triggerHaptic } from "../../lib/haptics"
import { Button } from "./button"
import { DrawerClose, DrawerContent, DrawerDescription, DrawerHandle, DrawerTitle } from "./drawer"

type MobileSheetSurfaceProps = {
  title?: React.ReactNode
  description?: React.ReactNode
  search?: React.ReactNode
  footer?: React.ReactNode
  children: React.ReactNode
  className?: string
  headerClassName?: string
  bodyClassName?: string
  footerClassName?: string
  maxHeightClassName?: string
  showCloseButton?: boolean
  closeLabel?: string
}

function MobileSheetSurface({
  title,
  description,
  search,
  footer,
  children,
  className,
  headerClassName,
  bodyClassName,
  footerClassName,
  // Size to content (so short sheets aren't a tall box with floating content), with a floor so a one-option
  // sheet still reads as a sheet (not a thin strip), capped so tall content scrolls.
  maxHeightClassName = "min-h-[7rem] max-h-[min(85dvh,32rem)]",
  showCloseButton = false,
  closeLabel = "Close",
}: MobileSheetSurfaceProps) {
  const hasHeader = Boolean(title || description || search || showCloseButton)
  const hasTitle = Boolean(title)
  const hasDescription = Boolean(description)
  const hasFooter = Boolean(footer)

  return (
    <DrawerContent
      className={cn(
        "flex min-h-0 flex-col overflow-hidden px-0 pb-0 pt-0",
        maxHeightClassName,
        className,
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {hasHeader ? (
          <div
            className={cn(
              "sticky top-0 z-20 border-b border-border/70 bg-card px-4 pb-3 pt-3",
              headerClassName,
            )}
          >
            {/* The whole header is a drag-to-dismiss zone: a transparent Vaul handle fills it (above the static
                title so a title-drag dismisses); interactive bits (close, search) sit above it via z-10. */}
            <DrawerHandle className="!absolute !inset-0 !z-0 !block !size-full !translate-x-0 !translate-y-0 !rounded-none !bg-transparent" />
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1 space-y-1">
                <DrawerTitle className={hasTitle ? "text-base font-medium leading-none" : "sr-only"}>
                  {hasTitle ? title : "Sheet"}
                </DrawerTitle>
                <DrawerDescription className={hasDescription ? "text-muted-foreground text-sm" : "sr-only"}>
                  {hasDescription ? description : "Sheet content"}
                </DrawerDescription>
              </div>
              {showCloseButton ? (
                <DrawerClose asChild>
                  <Button
                    variant="ghost"
                    className="relative z-10 -mr-1 shrink-0 size-12 md:size-10 [&_svg]:size-10 md:[&_svg]:size-7"
                  >
                    <XIcon />
                    <span className="sr-only">{closeLabel}</span>
                  </Button>
                </DrawerClose>
              ) : null}
            </div>
            {search ? <div className="relative z-10 pt-3">{search}</div> : null}
          </div>
        ) : null}

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-y-auto [&>*]:shrink-0",
            bodyClassName,
            // No footer → the body's last content would sit flush against the sheet edge; pad it.
            !hasFooter && "pb-[var(--mobile-footer-pb,1.25rem)]",
          )}
        >
          {children}
        </div>

        {hasFooter ? (
          <div
            className={cn(
              "sticky bottom-0 z-20 mt-auto shrink-0 border-t border-border/70 bg-card px-4 pt-3 pb-[var(--mobile-footer-pb)]",
              footerClassName,
            )}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </DrawerContent>
  )
}

type MobileListRowProps = {
  children: React.ReactNode
  className?: string
  selected?: boolean
  disabled?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  leading?: React.ReactNode
  trailing?: React.ReactNode
  ariaLabel?: string
  type?: "button" | "div"
}

function MobileListRow({
  children,
  className,
  selected = false,
  disabled = false,
  onClick,
  leading,
  trailing,
  ariaLabel,
  type = "button",
}: MobileListRowProps) {
  const content = (
    <>
      {leading ? <span className="flex shrink-0 items-center justify-center">{leading}</span> : null}
      <span className="min-w-0 flex-1 text-left">{children}</span>
      {trailing ? <span className="flex shrink-0 items-center justify-center">{trailing}</span> : null}
    </>
  )

  if (type === "div") {
    return (
      <div
        aria-label={ariaLabel}
        data-selected={selected ? "true" : undefined}
        className={cn(
          "text-foreground/90 flex w-full items-center gap-3 border-b border-border/40 px-4 py-3 text-left text-[15px] leading-5 last:border-b-0",
          selected && "text-primary",
          className,
        )}
      >
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      data-selected={selected ? "true" : undefined}
      onClick={(event) => {
        // Subtle native-feeling tap when picking from a bottom-sheet list (Android; no-op elsewhere).
        triggerHaptic("selection")
        onClick?.(event)
      }}
      className={cn(
        "text-foreground/90 flex w-full items-center gap-3 border-b border-border/40 px-4 py-3 text-left text-[15px] leading-5 transition-colors last:border-b-0 disabled:pointer-events-none disabled:opacity-50",
        selected && "text-primary",
        className,
      )}
    >
      {content}
    </button>
  )
}

type MobileListEmptyProps = {
  children: React.ReactNode
  className?: string
}

function MobileListEmpty({ children, className }: MobileListEmptyProps) {
  return (
    <div className={cn("text-muted-foreground px-4 py-8 text-center text-sm", className)}>{children}</div>
  )
}

type MobileListSeparatorProps = {
  className?: string
}

function MobileListSeparator({ className }: MobileListSeparatorProps) {
  return <div className={cn("bg-border/40 h-px w-full", className)} />
}

type MobileListGroupLabelProps = {
  children: React.ReactNode
  className?: string
}

function MobileListGroupLabel({ children, className }: MobileListGroupLabelProps) {
  return <div className={cn("text-muted-foreground px-4 pb-2 pt-4 text-[11px] font-medium uppercase tracking-[0.12em]", className)}>{children}</div>
}

export {
  MobileSheetSurface,
  MobileListRow,
  MobileListEmpty,
  MobileListSeparator,
  MobileListGroupLabel,
}
